import { useEffect, useRef, useState } from 'react';
import Stomp, { Client, Subscription } from 'stompjs';
import SockJS from 'sockjs-client';
import { fetchSocketToken } from '../helpers/fetchSocketToken';

const MAX_RETRIES = 5;
const RETRY_BASE_DELAY_MS = 500;
const DEFAULT_HEART_BEAT_MS = 25000;
// Set per environment via REACT_APP_STOMP_HEARTBEAT_MS; anything that isn't a positive number falls back.
const configuredHeartBeat = Number(window.__ENV__?.REACT_APP_STOMP_HEARTBEAT_MS);
const HEART_BEAT = configuredHeartBeat > 0 ? configuredHeartBeat : DEFAULT_HEART_BEAT_MS;

interface UseResilientStompClientProps {
	url: string;
	onConnect?: (client: Client) => void;
	onDisconnect?: () => void;
	headers?: Record<string, string>;
}

export function useResilientStompClient({ url, onConnect, onDisconnect, headers = {} }: UseResilientStompClientProps) {
	const clientRef = useRef<Client | null>(null);
	const [connected, setConnected] = useState(false);
	const retryCount = useRef(0);
	const retryTimeout = useRef<NodeJS.Timeout | null>(null);
	const activeSubscriptions = useRef<Map<string, Subscription>>(new Map());
	const reconnecting = useRef(false);
	const stopReconnecting = useRef(false);
	const cancelledRef = useRef(false);
	const urlRef = useRef(url);
	const headersRef = useRef(headers);
	urlRef.current = url;
	headersRef.current = headers;

	const clearSubscriptions = () => {
		activeSubscriptions.current.forEach((sub) => sub.unsubscribe());
		activeSubscriptions.current.clear();
	};

	const connect = async () => {
		if (reconnecting.current || stopReconnecting.current) return;

		reconnecting.current = true;

		const token = await fetchSocketToken();

		if (cancelledRef.current) {
			reconnecting.current = false;
			return;
		}

		if (!token) {
			reconnecting.current = false;
			setConnected(false);
			clientRef.current = null;

			if (retryCount.current < MAX_RETRIES && document.visibilityState === 'visible') {
				const delay = RETRY_BASE_DELAY_MS * Math.pow(2, retryCount.current);
				retryTimeout.current = setTimeout(() => {
					retryCount.current += 1;
					void connect();
				}, delay);
			} else {
				stopReconnecting.current = true;
				onDisconnect?.();
			}
			return;
		}

		const baseHeaders = headersRef.current ?? {};
		const connectHeaders: Record<string, string> = {
			...Object.fromEntries(Object.entries(baseHeaders).map(([k, v]) => [k, String(v)])),
			Authorization: `Bearer ${token}`,
		};

		const socket = new SockJS(urlRef.current);
		const stompClient = Stomp.over(socket);
		stompClient.debug = () => {};
		stompClient.heartbeat.outgoing = HEART_BEAT;
		stompClient.heartbeat.incoming = HEART_BEAT;

		stompClient.connect(
			connectHeaders,
			() => {
				if (cancelledRef.current) {
					stompClient.disconnect(() => {});
					reconnecting.current = false;
					return;
				}
				reconnecting.current = false;
				retryCount.current = 0;
				setConnected(true);
				clientRef.current = stompClient;
				clearSubscriptions();

				if (onConnect) {
					const safeSubscribe = (destination: string, callback: (msg: any) => void, force = false) => {
						if (!force && activeSubscriptions.current.has(destination)) {
							return;
						}
						const sub = stompClient.subscribe(destination, callback);
						activeSubscriptions.current.set(destination, sub);
					};

					onConnect({ ...stompClient, subscribe: safeSubscribe } as Client);
				}
			},
			(error) => {
				reconnecting.current = false;
				setConnected(false);
				clientRef.current = null;
				console.error('STOMP connection error:', error);

				if (retryCount.current < MAX_RETRIES && document.visibilityState === 'visible') {
					const delay = RETRY_BASE_DELAY_MS * Math.pow(2, retryCount.current);
					retryTimeout.current = setTimeout(() => {
						retryCount.current += 1;
						void connect();
					}, delay);
				} else {
					stopReconnecting.current = true;
					onDisconnect?.();
				}
			},
		);
	};

	const disconnect = () => {
		if (retryTimeout.current) clearTimeout(retryTimeout.current);
		if (clientRef.current?.connected) {
			clientRef.current.disconnect(() => {
				clearSubscriptions();
				setConnected(false);
				clientRef.current = null;
			});
		}
	};

	const connectHeadersKey = JSON.stringify(headers ?? {});

	useEffect(() => {
		cancelledRef.current = false;
		void connect();
		return () => {
			cancelledRef.current = true;
			disconnect();
		};
	}, [url, connectHeadersKey]);

	useEffect(() => {
		const handleVisibility = () => {
			if (document.visibilityState === 'visible' && !clientRef.current?.connected) {
				console.warn('🔁 Reconnecting STOMP due to tab visibility');

				if (stopReconnecting.current) {
					retryCount.current = 0;
					stopReconnecting.current = false;
				}

				void connect();
			}
		};

		document.addEventListener('visibilitychange', handleVisibility);
		return () => document.removeEventListener('visibilitychange', handleVisibility);
	}, []);

	useEffect(() => {
		const handleOnline = () => {
			console.warn('🌐 Network back ONLINE');
			// Allow reconnect attempts again
			stopReconnecting.current = false;
			retryCount.current = 0;
			if (!clientRef.current?.connected) {
				void connect();
			}
		};
		window.addEventListener('online', handleOnline);
		return () => {
			window.removeEventListener('online', handleOnline);
		};
	}, []);

	return {
		stompClient: clientRef.current,
		connected,
		reconnect: connect,
	};
}
